/**
 * Stack & Save — pricing fold (sections/stack-and-save.liquid).
 *
 * Regular price is the Shopify variant price; the discounted price is the
 * variant's `custom.special_price_ui` metafield. Liquid has already resolved
 * both onto each row checkbox as integer cents, normalising "no special price"
 * to the regular price so per-item discount is simply regular - special.
 *
 * All arithmetic stays in integer cents and formats only at the edge, so
 * non-round specials (e.g. $108.50) are exact — no float drift.
 *
 * Selection state lives in stack-and-save.js; this module reads the DOM, folds,
 * and writes the summary card. Everything but renderSummary is pure.
 */

import { formatMoney } from '../utils/format-money.js';

const DEFAULT_MONEY_FORMAT = '${{amount}}';

const toCents = (value) => {
  const cents = Number(value);
  return Number.isFinite(cents) ? cents : 0;
};

/** One row's contribution, in cents. */
export const readRow = (checkbox) => {
  const regular = toCents(checkbox.dataset.stackPrice);
  const special = Number.isFinite(Number(checkbox.dataset.stackSpecialPrice))
    ? toCents(checkbox.dataset.stackSpecialPrice)
    : regular;

  return {
    regular,
    special,
    enabled: !checkbox.disabled,
    selected: checkbox.checked && !checkbox.disabled,
  };
};

const EMPTY_TOTALS = {
  total: 0,
  offer: 0,
  discount: 0,
  maxTotal: 0,
  maxOffer: 0,
  maxDiscount: 0,
};

/**
 * Folds rows into the six figures the card needs. `total`/`offer` count selected
 * rows; the `max*` trio counts every enabled row — a sold-out row must not
 * inflate the denominator, or the progress bar could never reach 100%.
 */
export const sumRows = (rows) =>
  rows.reduce((acc, row) => {
    const selected = row.selected ? row : { regular: 0, special: 0 };
    const available = row.enabled ? row : { regular: 0, special: 0 };

    return {
      total: acc.total + selected.regular,
      offer: acc.offer + selected.special,
      discount: acc.discount + (selected.regular - selected.special),
      maxTotal: acc.maxTotal + available.regular,
      maxOffer: acc.maxOffer + available.special,
      maxDiscount: acc.maxDiscount + (available.regular - available.special),
    };
  }, EMPTY_TOTALS);

/** Progress toward the maximum achievable discount. Display-only ratio. */
export const barPercent = (discount, maxDiscount) => {
  if (maxDiscount <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((discount / maxDiscount) * 100)));
};

/**
 * formatMoney omits thousands separators, but Liquid's `money` filter adds them.
 * Group here so the server's first paint and later client repaints agree above
 * $1,000 — without touching the shared util's eight other consumers.
 */
export const formatSummary = (cents, moneyFormat = DEFAULT_MONEY_FORMAT) =>
  formatMoney(cents, moneyFormat).replace(
    /\d+/,
    (digits) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  );

const setText = (root, selector, text) => {
  const node = root.querySelector(selector);
  if (node) node.textContent = text;
};

/** The single impure step: paints the summary card. */
export const renderSummary = (root, totals, moneyFormat) => {
  const { total, offer, discount, maxDiscount } = totals;
  const money = (cents) => formatSummary(cents, moneyFormat);

  setText(root, '[data-stack-savings]', money(discount));
  setText(root, '[data-stack-total]', money(total));
  setText(root, '[data-stack-discount]', discount > 0 ? `-${money(discount)}` : money(0));
  setText(root, '[data-stack-pay]', money(offer));

  const percent = barPercent(discount, maxDiscount);
  const bar = root.querySelector('[data-stack-bar]');
  if (bar) {
    bar.style.setProperty('--stack-bar-fill', `${percent}%`);
    bar.setAttribute('aria-valuenow', String(percent));
  }

  // Prefix comes from a data attribute, never from the rendered text — otherwise
  // repeated renders would append money onto money.
  const prefix = root.dataset.stackSelectAllPrefix;
  if (prefix) setText(root, '[data-stack-select-all-text]', `${prefix} ${money(maxDiscount)}`);

  const submit = root.querySelector('[data-stack-submit]');
  if (submit) submit.disabled = total === 0;
};

/** Reads every row, folds, repaints. Called on init and on every change. */
export default function recomputePricing(root) {
  const moneyFormat = root.dataset.moneyFormat || DEFAULT_MONEY_FORMAT;
  const rows = Array.from(root.querySelectorAll('[data-stack-checkbox]')).map(readRow);

  renderSummary(root, sumRows(rows), moneyFormat);
}
